/**
 * @file     RentInputsCard.tsx
 * @purpose  Rent-side and shared inputs grouped under Current rent and Insurance + assumptions sub-sections.
 */
'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { NumericField } from './NumericField';
import type { RentVsBuyInputs } from './rentVsBuyMath';

type Props = {
  inputs: RentVsBuyInputs;
  patch: (next: Partial<RentVsBuyInputs>) => void;
  /** When true, force single-column field layout. Used when the card sits in a narrow rail. */
  dense?: boolean;
};

const SectionHeading = ({ children }: { children: React.ReactNode }) => {
  const { theme } = useAppTheme();
  return (
    <Typography
      sx={{
        fontSize: 11,
        color: theme.semanticColors.textMuted,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        mb: 1.25,
      }}
    >
      {children}
    </Typography>
  );
};

export const RentInputsCard = ({ inputs, patch, dense = false }: Props) => {
  const { theme } = useAppTheme();
  const fieldGridSx = dense
    ? {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 2,
        '@media (min-width: 1200px)': { gridTemplateColumns: '1fr' },
        '@media (max-width: 599px)': { gridTemplateColumns: '1fr' },
      }
    : {
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 2,
      };
  return (
    <Box
      sx={{
        bgcolor: theme.semanticColors.surfaceCard,
        borderRadius: `${theme.layout.radius.md}px`,
        border: `1px solid ${theme.semanticColors.borderSubtle}`,
        p: { xs: 2, sm: 2.5 },
      }}
    >
      <Typography
        sx={{
          fontFamily: theme.typography.fontFamilyHeading,
          fontSize: theme.typography.h4,
          fontWeight: 600,
          color: theme.semanticColors.textPrimary,
          letterSpacing: '-0.01em',
          mb: 0.5,
        }}
      >
        Rent side
      </Typography>
      <Typography sx={{ fontSize: theme.typography.bodySm, color: theme.semanticColors.textSecondary, mb: 2.5 }}>
        Your costs if you rent, plus the assumptions used for the renter's investment alternative.
      </Typography>

      <SectionHeading>Current rent</SectionHeading>
      <Box sx={fieldGridSx}>
        <NumericField
          label="Annual rent"
          value={inputs.rentMonthly}
          onChange={(v) => patch({ rentMonthly: Math.max(0, v) })}
          prefix="$"
          suffix="/ month"
          min={0}
        />
        <NumericField
          label="Annual rent increase"
          value={inputs.rentGrowthPct}
          onChange={(v) => patch({ rentGrowthPct: Math.max(-5, Math.min(v, 15)) })}
          suffix="% / yr"
          step={0.1}
        />
      </Box>

      <Box sx={{ mt: 2.5 }}>
        <SectionHeading>Insurance and assumptions</SectionHeading>
        <Box sx={fieldGridSx}>
          <NumericField
            label="Renter's insurance"
            value={inputs.rentersInsuranceMonthly}
            onChange={(v) => patch({ rentersInsuranceMonthly: Math.max(0, v) })}
            prefix="$"
            suffix="/ month"
            min={0}
          />
          <NumericField
            label="Investment return"
            value={inputs.investmentReturnPct}
            onChange={(v) => patch({ investmentReturnPct: Math.max(0, Math.min(v, 25)) })}
            suffix="% / yr"
            step={0.1}
            helper="On the cash you'd otherwise put toward a down payment"
          />
          <NumericField
            label="Marginal tax rate"
            value={inputs.marginalTaxPct}
            onChange={(v) => patch({ marginalTaxPct: Math.max(0, Math.min(v, 60)) })}
            suffix="%"
            step={0.5}
            helper="Applied to investment gains"
          />
          <NumericField
            label="Time horizon"
            value={inputs.horizonYears}
            onChange={(v) => patch({ horizonYears: Math.max(1, Math.min(Math.round(v), 30)) })}
            suffix="years"
            step={1}
            min={1}
            max={30}
          />
        </Box>
      </Box>
    </Box>
  );
};
